import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { HealthscoreCockpitView, BRAND, IST_ZUSTAND_CATEGORIES, VERHALTEN_CATEGORIES, } from '@sitzkrieger/healthscore-cockpit';
import { generateUrl } from '../../../Utilities/api';
function formatDateTime(iso) {
    const date = new Date(iso);
    return `${date.toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: '2-digit' })} ${date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
}
function formatSourceSuffix(dataSources) {
    const parts = [];
    if (dataSources.includes('trainer'))
        parts.push('Messung');
    if (dataSources.includes('fragebogen'))
        parts.push('Fragebogen');
    return parts.length > 0 ? parts.join(' + ') : 'Fragebogen';
}
function lowestCategory(categoryScores, candidates) {
    let lowestScore = 101;
    let lowestCategoryKey = null;
    for (const categoryKey of candidates) {
        const entry = categoryScores[categoryKey];
        if (entry !== undefined && entry.score < lowestScore) {
            lowestScore = entry.score;
            lowestCategoryKey = categoryKey;
        }
    }
    return lowestCategoryKey;
}
const HealthscoreCockpitV2 = ({ testUrl }) => {
    const [questionnaires, setQuestionnaires] = useState([]);
    const [reportSummaries, setReportSummaries] = useState([]);
    const [selectedKey, setSelectedKey] = useState(null);
    const [fullReport, setFullReport] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isReportLoading, setIsReportLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isOpeningReport, setIsOpeningReport] = useState(false);
    // Opens the member's healthscore "Bericht" (result page + PDF download) in a new
    // tab. The signed result link is minted server-side from the questionnaire token.
    const openReport = async (token) => {
        setIsOpeningReport(true);
        // Open the tab synchronously inside the click gesture so popup blockers allow
        // it, then navigate it once the signed link resolves. Detaching opener keeps
        // the noopener guarantee we would otherwise lose by holding a window handle.
        const reportWindow = window.open('about:blank', '_blank');
        if (reportWindow !== null) {
            reportWindow.opener = null;
        }
        try {
            const response = await fetch(generateUrl('sitzkrieger_v2_myareaapi_healthscore_v2_result_link', { token }), { credentials: 'same-origin' });
            const data = response.ok ? await response.json() : null;
            const url = data?.url;
            if (url !== undefined && url !== '') {
                if (reportWindow !== null) {
                    reportWindow.location.href = url;
                }
                else {
                    window.open(url, '_blank', 'noopener');
                }
            }
            else {
                reportWindow?.close();
            }
        }
        catch {
            // Best-effort — the cockpit stays usable if the link cannot be built.
            reportWindow?.close();
        }
        finally {
            setIsOpeningReport(false);
        }
    };
    // Load all data sources and auto-select the newest entry.
    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [questionnaireResponse, reportResponse] = await Promise.all([
                    fetch(generateUrl('sitzkrieger_v2_myareaapi_healthscore_v2_history'), { credentials: 'same-origin' }),
                    fetch(generateUrl('sitzkrieger_v2_myareaapi_healthscore_v2_reports'), { credentials: 'same-origin' }),
                ]);
                const questionnaireList = questionnaireResponse.ok ? await questionnaireResponse.json() : [];
                const reportList = reportResponse.ok ? await reportResponse.json() : [];
                setQuestionnaires(questionnaireList);
                setReportSummaries(reportList);
            }
            catch {
                setHasError(true);
            }
            finally {
                setIsLoading(false);
            }
        };
        void fetchAll();
    }, []);
    // When a report is selected, lazy-load the full report. For a questionnaire selection nothing extra is fetched.
    useEffect(() => {
        if (selectedKey === null || !selectedKey.startsWith('report:')) {
            setFullReport(null);
            return;
        }
        const uuid = selectedKey.slice('report:'.length);
        setIsReportLoading(true);
        void fetch(generateUrl('sitzkrieger_v2_myareaapi_healthscore_v2_report', { uuid }), { credentials: 'same-origin' })
            .then(response => response.ok ? response.json() : null)
            .then(data => { setFullReport(data); setIsReportLoading(false); })
            .catch(() => { setFullReport(null); setIsReportLoading(false); });
    }, [selectedKey]);
    // Unified dropdown list — reports + questionnaires merged, newest first.
    const unifiedEntries = useMemo(() => {
        const reportEntries = reportSummaries.map(report => ({
            key: `report:${report.uuid}`,
            kind: 'report',
            referenceId: report.uuid,
            generatedAt: report.generatedAt,
            score: report.overallScore,
            label: `${formatDateTime(report.generatedAt)} — Score ${report.overallScore} (${formatSourceSuffix(report.dataSources)})`,
        }));
        const consumedTokens = new Set(reportSummaries.map(report => report.questionnaireToken).filter((token) => token != null));
        const questionnaireEntries = questionnaires
            // Hide questionnaires already consumed by a report, matched by exact token.
            .filter(questionnaire => !consumedTokens.has(questionnaire.token))
            .map(questionnaire => ({
            key: `questionnaire:${questionnaire.token}`,
            kind: 'questionnaire',
            referenceId: questionnaire.token,
            generatedAt: questionnaire.finishedAt,
            score: questionnaire.score,
            label: `${formatDateTime(questionnaire.finishedAt)} — Score ${questionnaire.score} (Fragebogen)`,
        }));
        return [...reportEntries, ...questionnaireEntries].sort((a, b) => b.generatedAt.localeCompare(a.generatedAt));
    }, [reportSummaries, questionnaires]);
    // Default (and self-heal) the selection to the newest entry actually in the dropdown.
    // Selecting from the raw lists could point at a questionnaire hidden by the consumed-token
    // dedup, leaving the cockpit empty until a manual reselect.
    useEffect(() => {
        if (unifiedEntries.length === 0) {
            return;
        }
        if (selectedKey === null || !unifiedEntries.some(entry => entry.key === selectedKey)) {
            setSelectedKey(unifiedEntries[0].key);
        }
    }, [unifiedEntries, selectedKey]);
    // Resolve the active selection.
    const selectedEntry = selectedKey !== null ? unifiedEntries.find(entry => entry.key === selectedKey) ?? null : null;
    const isReportSelected = selectedEntry !== null && selectedEntry.kind === 'report';
    const isQuestionnaireSelected = selectedEntry !== null && selectedEntry.kind === 'questionnaire';
    const currentQuestionnaire = isQuestionnaireSelected && selectedEntry !== null
        ? questionnaires.find(questionnaire => questionnaire.token === selectedEntry.referenceId) ?? null
        : null;
    const categoryScores = isReportSelected && fullReport !== null
        ? fullReport.categoryScores
        : currentQuestionnaire !== null
            ? Object.fromEntries(Object.entries(currentQuestionnaire.groupScores).map(([key, value]) => [key, { score: Math.round(value), source: 'questionnaire' }]))
            : {};
    const overallScore = isReportSelected && fullReport !== null
        ? fullReport.overallScore
        : currentQuestionnaire !== null
            ? currentQuestionnaire.score
            : null;
    const fokusIstZustand = isReportSelected && fullReport !== null
        ? fullReport.fokusIstZustand
        : lowestCategory(categoryScores, IST_ZUSTAND_CATEGORIES);
    const fokusVerhalten = isReportSelected && fullReport !== null
        ? fullReport.fokusVerhalten
        : lowestCategory(categoryScores, VERHALTEN_CATEGORIES);
    const measurementTargets = isReportSelected && fullReport !== null ? fullReport.measurementTargets : [];
    // History for the detail panel: all reports + questionnaires mapped to HealthReportSummary.
    const historyReports = useMemo(() => {
        const fromReports = reportSummaries.map(report => ({
            uuid: report.uuid,
            overallScore: report.overallScore,
            leverCategory: null,
            focusCategories: [report.fokusIstZustand, report.fokusVerhalten].filter((category) => category !== null),
            dataSources: report.dataSources,
            generatedAt: report.generatedAt,
            generatedBy: '',
            categoryScores: report.categoryScores,
        }));
        const fromQuestionnaires = questionnaires.map(questionnaire => ({
            uuid: questionnaire.token,
            overallScore: questionnaire.score,
            leverCategory: null,
            focusCategories: [],
            dataSources: ['questionnaire'],
            generatedAt: questionnaire.finishedAt,
            generatedBy: '',
            categoryScores: Object.fromEntries(Object.entries(questionnaire.groupScores).map(([key, value]) => [key, Math.round(value)])),
        }));
        return [...fromReports, ...fromQuestionnaires].sort((a, b) => b.generatedAt.localeCompare(a.generatedAt));
    }, [reportSummaries, questionnaires]);
    const focusCategories = [fokusIstZustand, fokusVerhalten].filter((category) => category !== null);
    const showSkeleton = isLoading || isReportLoading;
    const fragebogenOnly = isQuestionnaireSelected || (fullReport !== null && !fullReport.dataSources.includes('trainer'));
    if (hasError)
        return <></>;
    return (<div className="flex flex-col gap-6">
            {/* Entry dropdown + CTA */}
            <div className="flex items-center gap-3 flex-wrap">
                {!isLoading && unifiedEntries.length > 0 && (<select value={selectedKey ?? ''} onChange={event => setSelectedKey(event.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2" style={{ minWidth: 280 }}>
                        {unifiedEntries.map(entry => (<option key={entry.key} value={entry.key}>{entry.label}</option>))}
                    </select>)}
                {!isLoading && unifiedEntries.length === 0 && (<span className="text-sm text-gray-400 italic">Noch kein Eintrag vorhanden</span>)}

                {!isLoading && selectedEntry !== null && (() => {
            // Report entries link via the questionnaire they consumed (the
            // standalone result page is record-based). Pure-Messung reports
            // have no record, so no page to open.
            const resultRecordToken = selectedEntry.kind === 'questionnaire'
                ? selectedEntry.referenceId
                : fullReport?.questionnaireToken ?? null;
            return resultRecordToken !== null ? (<button type="button" onClick={() => void openReport(resultRecordToken)} disabled={isOpeningReport} className="px-4 py-2 rounded-lg text-sm font-medium border transition-colors shrink-0 disabled:opacity-60" style={{ borderColor: BRAND.blue, color: BRAND.blue }}>
                            {isOpeningReport ? 'Öffne…' : 'Zum Healthscore-Bericht'}
                        </button>) : null;
        })()}

                <a href={testUrl} className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ml-auto shrink-0" style={{ backgroundColor: BRAND.blue }}>
                    Neuen Test starten
                </a>
            </div>

            <HealthscoreCockpitView categoryScores={showSkeleton ? {} : categoryScores} overallScore={showSkeleton ? null : overallScore} leverCategory={fokusIstZustand} behaviorLeverCategory={fokusVerhalten} focusCategories={focusCategories} measurementTargets={measurementTargets} reports={historyReports} fragebogenOnly={fragebogenOnly} audience="customer"/>
        </div>);
};
HealthscoreCockpitV2.displayName = 'HealthscoreCockpitV2';
export default HealthscoreCockpitV2;
